'use client';

import { FC, ReactNode, useState } from 'react';
import classNames from 'classnames';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';
import useBoxes from 'networking/mindfeed/hooks/useBoxes';
import { useSearchState } from 'xstate/hooks/useSearchState';
import {
  setSearchBoxFilter,
  setSearchCollectionFilter,
} from 'xstate/actors/SearchActor';
import { useTranslations } from 'next-intl';
import { useBoxLabels } from 'hooks/useBoxLabels';
import { SearchField } from 'elements/SearchField/SearchField';
import BoxIcon from 'assets/icons/icon-box.svg';
import CollectionIcon from 'assets/icons/icon-collection.svg';
import { contrastingForeground } from 'utils/ColorUtils';
import type { Box } from 'models/Box';
import type { Collection } from 'models/Collection';

// Longer lists get a search field at the top of the menu.
const SEARCHABLE_FROM = 8;

type Option = { id: string; label: string; glyph: ReactNode };

// A pill that opens a menu of boxes or collections, in the style of the
// composer's type picker: an icon on each row, the chosen one tinted.
const FilterMenu: FC<{
  icon: ReactNode;
  label: string;
  active: boolean;
  disabled?: boolean;
  // Why the pill is off, shown on hover.
  disabledHint?: string;
  // The "Any …" row at the top, which clears the filter.
  anyLabel: string;
  anyGlyph: ReactNode;
  options: Option[];
  searchPlaceholder: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}> = ({
  icon,
  label,
  active,
  disabled,
  disabledHint,
  anyLabel,
  anyGlyph,
  options,
  searchPlaceholder,
  selectedId,
  onSelect,
}) => {
  const t = useTranslations();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;

  return (
    <Listbox value={selectedId} onChange={onSelect} disabled={disabled}>
      <ListboxButton
        onClick={() => setQuery('')}
        title={disabled ? disabledHint : undefined}
        className={styles.pill.button}
      >
        <span
          className={classNames(
            styles.pill.circle,
            active ? styles.pill.circleActive : styles.pill.circleIdle,
          )}
        >
          {icon}
        </span>
        <span className={styles.pill.label}>{label}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={styles.pill.chevron}
          aria-hidden="true"
        />
      </ListboxButton>

      <ListboxOptions
        transition
        anchor={{ to: 'bottom start', gap: '6px' }}
        className={styles.menu.panel}
      >
        {options.length >= SEARCHABLE_FROM && (
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
          />
        )}

        <div className={styles.menu.list}>
          {/* While searching, only the matching names show. */}
          {!needle && (
            <>
              <FilterOption id={null} label={anyLabel} glyph={anyGlyph} />
              {options.length > 0 && <div className={styles.menu.separator} />}
            </>
          )}
          {filtered.map((option) => (
            <FilterOption key={option.id} {...option} />
          ))}
          {filtered.length === 0 && needle && (
            <p className={styles.menu.empty}>{t('common.noMatches')}</p>
          )}
        </div>
      </ListboxOptions>
    </Listbox>
  );
};

const FilterOption: FC<{
  id: string | null;
  label: string;
  glyph: ReactNode;
}> = ({ id, label, glyph }) => (
  <ListboxOption value={id} className={styles.option.row}>
    {glyph}
    <span className={styles.option.label}>{label}</span>
    <FontAwesomeIcon
      icon={faCheck}
      className={styles.option.check}
      aria-hidden="true"
    />
  </ListboxOption>
);

// The box's tile, as in the boxes column: its emoji, or the box icon on its
// colour.
const BoxGlyph: FC<{ box: Box }> = ({ box }) => {
  const color = box.display?.tile.color ?? null;
  const emoji = box.display?.tile.emoji ?? null;

  return (
    <span
      className={styles.glyph.tile}
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden="true"
    >
      {emoji ? (
        <span className={styles.glyph.emoji}>{emoji}</span>
      ) : (
        <BoxIcon
          className={styles.glyph.svg}
          style={color ? { color: contrastingForeground(color) } : undefined}
        />
      )}
    </span>
  );
};

const CollectionGlyph: FC<{ collection: Collection }> = ({ collection }) => (
  <span className={styles.glyph.well} aria-hidden="true">
    {collection.emoji ? (
      <span className={styles.glyph.emoji}>{collection.emoji}</span>
    ) : (
      <CollectionIcon className={styles.glyph.svg} />
    )}
  </span>
);

const AnyGlyph: FC<{ icon: ReactNode }> = ({ icon }) => (
  <span className={styles.glyph.well} aria-hidden="true">
    {icon}
  </span>
);

// "Search in [Any box ▾] [Any collection ▾]": the box and collection to
// search, kept in the shared SearchActor. The collection menu turns on once
// a box with collections is chosen.
export const SearchScopeFilters: FC<{ withLabel?: boolean }> = ({
  withLabel = false,
}) => {
  const t = useTranslations();
  const { boxName } = useBoxLabels();
  const { filterBoxId, filterCollectionId } = useSearchState();
  const { data: boxes = [] } = useBoxes();

  const selectedBox = boxes.find((b) => b.id === filterBoxId) ?? null;
  // Shares the dashboard's collections cache; runs only once a box is chosen.
  const { data: collections = [] } = useQuery(
    collectionsQuery(selectedBox?.id),
  );
  const selectedCollection = collections.find(
    (c) => c.id === filterCollectionId,
  );

  return (
    <div className={styles.row}>
      {withLabel && (
        <span className={styles.rowLabel}>{t('search.searchIn')}</span>
      )}
      <FilterMenu
        icon={<BoxIcon className={styles.pill.svg} aria-hidden="true" />}
        label={selectedBox ? boxName(selectedBox.name) : t('search.anyBox')}
        active={!!selectedBox}
        anyLabel={t('search.anyBox')}
        anyGlyph={<AnyGlyph icon={<BoxIcon className={styles.glyph.svg} />} />}
        options={boxes.map((box) => ({
          id: box.id,
          label: boxName(box.name),
          glyph: <BoxGlyph box={box} />,
        }))}
        searchPlaceholder={t('search.findBox')}
        selectedId={filterBoxId}
        onSelect={setSearchBoxFilter}
      />
      <FilterMenu
        icon={<CollectionIcon className={styles.pill.svg} aria-hidden="true" />}
        label={selectedCollection?.name || t('search.anyCollection')}
        active={!!selectedCollection}
        disabled={!selectedBox || collections.length === 0}
        disabledHint={
          selectedBox ? t('search.noCollections') : t('search.pickBoxFirst')
        }
        anyLabel={t('search.anyCollection')}
        anyGlyph={
          <AnyGlyph icon={<CollectionIcon className={styles.glyph.svg} />} />
        }
        options={collections.map((collection) => ({
          id: collection.id,
          label: collection.name,
          glyph: <CollectionGlyph collection={collection} />,
        }))}
        searchPlaceholder={t('search.findCollection')}
        selectedId={filterCollectionId}
        onSelect={setSearchCollectionFilter}
      />
    </div>
  );
};

// A short description of the current search scope, e.g. "Searching everywhere"
// or "Searching in My Box / Ideas". Shared reads dedupe with the filters above.
export const useSearchScopeLabel = (): string => {
  const t = useTranslations();
  const { boxName } = useBoxLabels();
  const { filterBoxId, filterCollectionId } = useSearchState();
  const { data: boxes = [] } = useBoxes();
  const box = boxes.find((b) => b.id === filterBoxId) ?? null;

  const { data: collections = [] } = useQuery(collectionsQuery(box?.id));

  if (!box) {
    return t('search.scope.everywhere');
  }
  const collection = collections.find((c) => c.id === filterCollectionId);
  const place = collection
    ? `${boxName(box.name)} / ${collection.name}`
    : boxName(box.name);
  return t('search.scope.in', { place });
};

const styles = {
  row: 'flex flex-wrap items-center gap-2',
  rowLabel: 'mr-1 text-[13px] text-ink-secondary',
  // Matches the composer's type pill: an icon in a circle, the name and a
  // chevron. The circle turns blue once a filter is set.
  pill: {
    button: classNames(
      'inline-flex max-w-56 items-center gap-2 rounded-full border border-stroke bg-surface-card',
      'py-[3px] pl-[3px] pr-2.5 hover:bg-ink-secondary/8 focus:outline-hidden',
      'focus-visible:ring-2 focus-visible:ring-accent-ink/40 data-open:bg-ink-secondary/8',
      'data-disabled:cursor-not-allowed data-disabled:opacity-50 data-disabled:hover:bg-surface-card',
    ),
    circle:
      'flex size-6 flex-none items-center justify-center rounded-full text-[11px]',
    circleIdle: 'bg-ink-secondary/12 text-ink-secondary',
    circleActive: 'bg-accent-fill text-white',
    label: 'truncate text-[13px] font-semibold text-ink-primary',
    svg: 'size-3.5',
    chevron: 'flex-none text-[9px] text-ink-secondary',
  },
  menu: {
    panel: classNames(
      'z-[60] flex w-72 flex-col rounded-xl bg-surface-overlay shadow-lg ring-1 ring-hairline',
      'focus:outline-hidden transition duration-100 ease-out data-closed:scale-95 data-closed:opacity-0',
    ),
    list: 'max-h-80 overflow-y-auto p-1.5',
    separator: 'mx-2 my-1 border-t border-hairline',
    empty: 'px-2.5 py-2 text-xs text-ink-tertiary',
  },
  option: {
    row: classNames(
      'group flex cursor-pointer select-none items-center gap-3 rounded-[10px] border border-transparent px-2 py-1.5',
      'data-focus:bg-ink-secondary/8',
      'data-selected:border-accent-tint-ink/20 data-selected:bg-accent-tint data-selected:data-focus:bg-accent-tint',
    ),
    label:
      'min-w-0 flex-1 truncate text-sm font-medium text-ink-primary group-data-selected:font-semibold',
    check: 'invisible text-xs text-accent-ink group-data-selected:visible',
  },
  glyph: {
    well: 'flex size-7 flex-none items-center justify-center rounded-lg bg-surface-field text-[13px] text-ink-secondary',
    tile: 'flex size-7 flex-none items-center justify-center rounded-lg border border-ink-secondary/25 bg-surface-card text-ink-secondary',
    emoji: 'text-[15px] leading-none',
    svg: 'size-4',
  },
};
